import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  FileText,
  Package,
  Tag,
  Loader2,
  MapPin,
  User,
} from 'lucide-react';
import styles from './ClientDetail.module.scss';
import { fetchClientDetail } from '../../api';
import { money } from '../../utils';
import { StatusPill } from '../StatusPill/StatusPill';
import { MultiInfoCard } from '../InfoCard/InfoCard';
import { InvoiceTable } from '../InvoiceTable/InvoiceTable';
import { PoTable } from '../PoTable/PoTable';
import { SkuTable } from '../SkuTable/SkuTable';
import type { Client, ClientDetailData } from '../../types';

interface ClientDetailProps {
  client: Client;
  onBack: () => void;
}

type TabKey = 'invoices' | 'pos' | 'skus';

const TABS: { key: TabKey; label: string; icon: typeof FileText }[] = [
  { key: 'invoices', label: 'Invoices', icon: FileText },
  { key: 'pos', label: 'Purchase orders', icon: Package },
  { key: 'skus', label: 'SKUs', icon: Tag },
];

export function ClientDetail({ client, onBack }: ClientDetailProps) {
  const [tab, setTab] = useState<TabKey>('invoices');
  const [detail, setDetail] = useState<ClientDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchClientDetail(client).then((d) => {
      if (alive) {
        setDetail(d);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [client]);

  return (
    <div>
      <button onClick={onBack} className={styles.backButton}>
        <ArrowLeft size={15} />
        All clients
      </button>

      <div className={styles.titleRow}>
        <div>
          <h1 className={styles.clientName}>{client.name}</h1>
          <div className={styles.clientMeta}>
            {client.id} · {client.billingEmail}
          </div>
        </div>
        <StatusPill status={client.status} />
      </div>

      <div className={styles.infoGrid}>
        <MultiInfoCard
          icon={User}
          title="Points of contact"
          items={client.pocs}
          renderItem={(p) => (
            <div>
              <div className={styles.pocName}>{p.name}</div>
              <div className={styles.pocTitle}>{p.title}</div>
              <div className={styles.pocMono}>{p.email}</div>
              <div className={styles.pocMono}>{p.phone}</div>
            </div>
          )}
        />

        <MultiInfoCard
          icon={MapPin}
          title="Billing addresses"
          items={client.billingAddresses}
          renderItem={(a) => (
            <div>
              <div className={styles.addressLabel}>{a.label}</div>
              <div className={styles.addressText}>{a.address}</div>
            </div>
          )}
        />

        <MultiInfoCard
          icon={MapPin}
          title="Delivery locations"
          items={client.locations}
          renderItem={(loc) => (
            <div>
              <div className={styles.locationName}>{loc.name}</div>
              <div className={styles.locationAddress}>{loc.address}</div>
            </div>
          )}
        />
      </div>

      <div className={styles.statsGrid}>
        {[
          {
            label: 'Total billed',
            value: money(client.totalBilled),
            warn: false,
          },
          {
            label: 'Outstanding',
            value: client.outstanding ? money(client.outstanding) : '—',
            warn: !!client.outstanding,
          },
          {
            label: 'Invoices on file',
            value: String(client.invoiceCount),
            warn: false,
          },
        ].map((stat, i) => (
          <div key={i} className={styles.statCard}>
            <div className={styles.statLabel}>{stat.label}</div>
            <div
              className={
                stat.warn
                  ? `${styles.statValue} ${styles.statValueWarn}`
                  : styles.statValue
              }
            >
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.tabs}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                active ? `${styles.tab} ${styles.tabActive}` : styles.tab
              }
            >
              <Icon size={14} />
              {t.label}
              {active && <span className={styles.tabUnderline} />}
            </button>
          );
        })}
      </div>

      <div className={styles.tabPanel}>
        {loading || !detail ? (
          <div className={styles.loadingRow}>
            <Loader2 size={15} className={styles.spinner} />
            Loading {tab}
          </div>
        ) : tab === 'invoices' ? (
          <InvoiceTable rows={detail.invoices} />
        ) : tab === 'pos' ? (
          <PoTable rows={detail.pos} />
        ) : (
          <SkuTable rows={detail.skuLines} />
        )}
      </div>
    </div>
  );
}
